export const calculateDuration = (start: string | Date, end: string | Date): number => {
	const startTime = new Date(start);
	const endTime = new Date(end);

	return (endTime.getTime() - startTime.getTime()) / (1000 * 60);
};

export const convertTimeToMinutes = (dateStr: string | Date): number => {
	const date = new Date(dateStr);
	const hours = date.getHours();
	const minutes = date.getMinutes();
	const totalMinutes = hours * 60 + minutes;

	return totalMinutes;
};

export const formatTime = (
	dateStr: string | Date,
	showMinutes: boolean = true,
	showAppend: boolean = false
): string => {
	const date = new Date(dateStr);
	const hours = date.getHours();
	const minutes = date.getMinutes();
	const formattedHours = hours % 12 || 12;
	const formattedMinutes = `:${minutes.toString().padStart(2, '0')}`;
	const append = hours >= 12 ? 'pm' : 'am';
	const formattedTime = `${formattedHours}${showMinutes ? formattedMinutes : ''} ${showAppend ? append : ''}`;

	return formattedTime;
};

export const formatSingleDate = (dateStr: string): string => {
	const date = formatDateTime(dateStr);
	const month = date.toLocaleString('default', { month: 'long' });
	const day = date.getDate();
	const year = date.getFullYear();

	const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
	let dayOfWeek = weekday[date.getDay()];

	return `${dayOfWeek} ${month} ${day}, ${year}`;
};

export function concatenateDateTime(date: string, time: string): string {
	return date + "T" + time;
}

export const formatDateTime = (dateStr: string): Date => {
	const [dateYear, dateMonth, dateDay] = dateStr?.split('-').map(Number);
	const date = new Date(dateYear, (dateMonth ?? 1) - 1, dateDay);
	return date;
};

export const getNextDay = (dateStr: string): string => {
	const formattedDate = formatDateTime(dateStr);
	formattedDate.setDate(formattedDate.getDate() + 1);
	const nextDate = formattedDate.toLocaleDateString('en-CA');

	return nextDate;
};

export const getPreviousDay = (dateStr: string): string => {
	const formattedDate = formatDateTime(dateStr);
	formattedDate.setDate(formattedDate.getDate() - 1);
	const previousDate = formattedDate.toLocaleDateString('en-CA');

	return previousDate;
};

export interface WeekRange {
	startDate: string;
	endDate: string;
	currentDate?: string;
}

interface WeekRangeForToday {
	startDate: string;
	endDate: string;
	currentDate: string;
}

export const getWeekStartEndDate = (): WeekRangeForToday => {
	const today = new Date();
	const dayOfWeek = today.getDay();
	const diffToSunday = today.getDate() - dayOfWeek;

	const startOfWeek = new Date(today.setDate(diffToSunday));
	startOfWeek.setHours(0, 0, 0, 0);

	const endOfWeek = new Date(startOfWeek);
	endOfWeek.setDate(endOfWeek.getDate() + 6);
	endOfWeek.setHours(23, 59, 59, 999);

	return {
		startDate: startOfWeek.toLocaleDateString(),
		endDate: endOfWeek.toLocaleDateString(),
		currentDate: new Date().toLocaleDateString('en-CA'),
	};
};

export const getWeekStartEndDateForDate = (dateStr: string): WeekRange => {
	const day = new Date(dateStr);
	const dayOfWeek = day.getDay();
	let startOfWeek: Date;

	if (dayOfWeek !== 6) {
		const diffToSunday = day.getDate() - dayOfWeek;
		startOfWeek = new Date(day.setDate(diffToSunday));
	} else {
		startOfWeek = new Date(day.setDate(day.getDate() + 1));
	}

	startOfWeek.setHours(0, 0, 0, 0);
	const endOfWeek = new Date(startOfWeek);
	endOfWeek.setDate(endOfWeek.getDate() + 6);
	endOfWeek.setHours(23, 59, 59, 999);

	return {
		startDate: startOfWeek.toLocaleDateString(),
		endDate: endOfWeek.toLocaleDateString(),
	};
};

export const getNextWeekStartEndDate = (
	startDateStr: string,
	endDateStr: string
): WeekRange => {
	const startDay = new Date(startDateStr);
	const nextWeekStart = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate() + 7);

	const endDay = new Date(endDateStr);
	const nextWeekEnd = new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() + 7);

	return {
		startDate: nextWeekStart.toLocaleDateString(),
		endDate: nextWeekEnd.toLocaleDateString(),
	};
};

export const getPreviousWeekStartEndDate = (
	startDateStr: string,
	endDateStr: string
): WeekRange => {
	const startDay = new Date(startDateStr);
	const prevWeekStart = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate() - 7);

	const endDay = new Date(endDateStr);
	const prevWeekEnd = new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() - 7);

	return {
		startDate: prevWeekStart.toLocaleDateString(),
		endDate: prevWeekEnd.toLocaleDateString(),
	};
};

export const formatDateRange = (startDateStr: string, endDateStr: string): string => {
	const startDate = formatDateTime(startDateStr);
	const endDate = formatDateTime(endDateStr);

	const startMonth = startDate.toLocaleString('default', { month: 'long' });
	const endMonth = endDate.toLocaleString('default', { month: 'long' });

	const startDay = startDate.getDate();
	const endDay = endDate.getDate();

	const startYear = startDate.getFullYear();
	const endYear = endDate.getFullYear();

	let dateRange = `${startMonth} ${startDay}`;

	if (startYear !== endYear) {
		dateRange += `, ${startYear} - ${endMonth} ${endDay}`;
	} else if (startMonth !== endMonth) {
		dateRange += ` - ${endMonth} ${endDay}`;
	} else {
		dateRange += ` - ${endDay}`;
	}

	return (dateRange += `, ${endYear}`);
};